//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  ChangeEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
} from '@tanstack/react-table';
import { format, parseISO, formatISO } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Ordenación server-side — columnas + campo backend
// Whitelist backend CAMPAIGN_SORTABLE_FIELDS: registerInitDate, creationDate, state, pkInitKm, pkEndKm, name, track, direction
const SORTABLE_COLUMNS = {
  state: 'state',
  name: 'name',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  track: 'track',
  direction: 'direction',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TdStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  ContainerButtonStyled,
  CampaignSearchContainer,
  ContainerStyled,
  FiltersWithLoadingStyled,
  ContainerLoader,
  StyledTypography,
} from './styles';
import {
  Th,
  Tbody,
  Title,
  Table,
  TextCell,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCalendarStyled,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
} from '../components/MainStyledComponents';

// Components
import { PaginationButtons } from '../components/PaginationButtons';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { SpecialTag } from '../../SpecialTag';
import { CheckBox } from '../../../inputs/CheckBox';
import { SelectionBanner } from '../components/SelectionBanner';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

// Types
import {
  columnID,
  columnNames,
  getColumnNames,
  getColumnId,
} from '../components/types';
import { tableProps, dataTable, option } from './types';
import {
  onChangeValue,
  Options,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import { variant, size } from '../../SpecialTag/types';
import { Order } from '../../../../types/orders';
import { tableIds, takeBase } from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import { ImperativeSearchBarInput } from '../../../inputs/SearchBar/types';
import InputText from '../../../inputs/InputText';
import InputPK from '../../../inputs/InputPK';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import { filterAndSortMultimediaUsers } from '../../../../utils/multimediaUserFilters';
import SearchBar from '../../../inputs/SearchBar';
import { getStateOptionsByRoleAndEntityType } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, listingFiltersByRole } from '../types';
import { Role } from '../../../../types/auth';

// Data
import { getTrackOptions } from '../../../../utils/catalogLabels';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { Loader } from '../../../inputs/Loader';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

/**
 * Por defecto la bandera marca la Order de la fila: la fila de campaña ya
 * lleva su id (`selectAllOrders` → `id: order.id`; el de la Campaign no llega a
 * la fila). Fuera del componente para que la referencia sea estable y no rehaga
 * las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const CampaignTable = ({
  titleText,
  data,
  setRowState,
  onReFetchData,
  onReFetchPagination,
  tableId,
  showButtonsPagination = true,
  setPageSize,
  rowSelection: propsRowSelection,
  onRowSelectionChange: propsOnRowSelectionChange,
  selectAllMode = false,
  excludedIds,
  onSelectAll,
  onClearSelection,
  selectedCount: propsSelectedCount,
  // Fuera de `props`: no son atributos del <table>.
  showFlagColumn = false,
  showOnlyFlaggedFilter = false,
  getFlagOrderId = defaultGetFlagOrderId,
  ...props
}: tableProps): JSX.Element => {
  // Modo multi-selección controlado por el padre (vista de históricos).
  // Si no llega `rowSelection`, la tabla se comporta como radio single-select.
  const isMultiSelect = propsRowSelection !== undefined;
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dataAll = data?.campaigns ?? mocksTable;
  const roleActive = data?.roleActive;
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputNameRef = useRef<ImperativeSearchBarInput>(null);
  const inputTrackRef = useRef<ImperativeRefInput>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);

  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [showFilters, setShowFilters] = useState(false);
  const [internalRowSelection, setInternalRowSelection] = useState({});
  const rowSelection = isMultiSelect
    ? propsRowSelection!
    : internalRowSelection;
  const setRowSelection = isMultiSelect
    ? propsOnRowSelectionChange!
    : setInternalRowSelection;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    name: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat con cursor compartido)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select)
    userIds: string[] | undefined; // multi-select creador (entidad doble Campaign.orders[0].userId)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador"
    teamIds: string[] | undefined;
    predefinedOrderId: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    track: string | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    name: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador
    includeInactive: boolean | undefined;
    teamIds: string[] | undefined;
    predefinedOrderId: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    track: string | undefined;
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    name: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    includeInactive: undefined,
    teamIds: undefined,
    predefinedOrderId: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
    track: undefined,
  });

  const pageSizeModal = isPortraitTablet
    ? showFilters
      ? 5
      : 8
    : isLandscapeTablet
      ? showFilters
        ? 5
        : 7
      : 8;

  // Tarea #11 Fase 2 — page-size selector centralizado en `useServerPageSize`.
  // Activo cuando la tabla se usa como listado principal: gestión de campañas
  // (CAMPAIGNS) o históricos de campañas (SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS).
  // En contextos modales (CAMPAIGN_MODAL) `enabled=false` y `takePagination`
  // cae al `fallbackTake` responsivo (pageSizeModal).
  const { takePagination, paginationProps } = useServerPageSize({
    tableId,
    enabled:
      tableId === tableIds.CAMPAIGNS ||
      tableId === tableIds.SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
    fallbackTake: pageSizeModal,
  });

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLandscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLandscapeTablet]);

  // NOTE: Default state filter removed from CampaignTable
  // Campaigns should show all records without default state filter.
  // Only Generic WorkOrders tables should apply default state filter.
  // @see BUG #9 fix - entityStateFiltering.ts

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    team: false,
  });
  useLayoutEffect(() => {
    setPageSize?.(pageSizeModal);
  }, [pageSizeModal]);

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
    }));
  }, [showPredefinedFilter]);

  useEffect(() => {
    const concessions = data?.filters?.concessions;
    const teams = data?.filters?.teams;
    setColumnVisibility(prev => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
      team: !!teams && teams.length > 0,
    }));
  }, [data?.filters?.concessions, data?.filters?.teams]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField,
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: registerInitDate + desc viaja como sortField: undefined EXPLÍCITO
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [activeSortField, sortDirection, dispatch, tableId, setCurrentServerPage, onReFetchData],
  );

  // Helper que evita la duplicación en las columnas ordenables.
  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId as keyof typeof translatedColumnNames]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn],
  );
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: isMultiSelect
          ? ({ table }) => (
              <InputRadioContainer>
                <CheckBox
                  name={'select-all'}
                  {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomeRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                  }}
                />
              </InputRadioContainer>
            )
          : undefined,
        cell: ({ row }) =>
          isMultiSelect ? (
            <InputRadioContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <CheckBox
                name={'select'}
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </InputRadioContainer>
          ) : (
            <InputRadioContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <InputRadioStyled
                name="inputRadio"
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </InputRadioContainer>
          ),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: sortableHeader('registerInitDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),
      columnHelper.accessor(getColumnId(translatedColumnNames.derivedEndDate.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.derivedEndDate.label}
          </TextHeader>
        ),
        cell: (info) => {
          const v = info.getValue() as string | null | undefined;
          return <TextCell size={'S'}>{v ? format(new Date(v), 'dd/MM/yyyy - HH:mm') : '—'}</TextCell>;
        },
        filterFn: 'dateFilter' as FilterFnOption<dataTable>,
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.name.id), {
        header: sortableHeader('name'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
        filterFn: 'includesString',
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.team.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.team.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.track.id), {
        header: sortableHeader('track'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
        header: sortableHeader('direction'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.user.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      // Sin columna "Registrado por": ese dato solo se pide al crear una
      // Actuación o un Accidente (el desplegable del formulario se renderiza
      // con `isAction || isAccident`), así que en Campañas la columna salía
      // vacía por construcción. Se coló al añadirla a las cuatro tablas de
      // golpe en 2026-07-23.
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
        }
      ),
    ],
    [
      isPortraitTablet,
      sizeTag,
      translatedColumnNames,
      sortableHeader,
      isMultiSelect,
      showFlagColumn,
      getFlagOrderId,
      t,
    ]
  );

  // Tarea #11 — Sincronizar pageSize de TanStack con `takePagination` (server).
  // CampaignTable se usa también en modal: ahí takePagination = pageSizeModal
  // (responsive 5/7/8) y queremos que TanStack también use ese valor.
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction, // New ROID filter function
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: isMultiSelect,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onChangeCampaign: ChangeEventHandler<HTMLInputElement> = useCallback(
    async (event) => {
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId:
          selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: event.target.value,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.name = event.target.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData.toDate,
      selectorData.fromDate,
      selectorData.name,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */

  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData.states,
      selectorData.name,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.states,
      selectorData.name,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort estable para que Apollo `keyArgs: [..., 'states']`
      // genere la misma key independientemente del orden de click.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: nextStates,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Multi-select creador (entidad doble Campaign.orders[0].userId).
  // Patrón canónico replicado de AccidentsTable.onChangeSelectedUserIds.
  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: nextUserIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.states,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      selectorData.includeInactive,
      dispatch,
      tableId,
    ],
  );

  // Toggle "Incluir inactivos en Creador". Sólo expande la lista del dropdown.
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: takePagination,
        cursor: '',
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive: next,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, includeInactive: next }));
    },
    [takePagination, selectorCursor, selectorData, dispatch, tableId],
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro». `selectorData` no lleva la clave
  // (arranca con un objeto propio, no con el cursor), así que el selector de
  // tamaño de página la toma del reflejo del cursor, que ya queda al día.
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  // Opciones del dropdown Creador (mismo patrón que InputRadioTagTable).
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filters?.users ?? [],
      selectorCursor?.includeInactive ?? false,
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id),
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filters?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (u: unknown): u is { id?: string; name?: string; surName?: string; isActive?: boolean } =>
          Boolean(u),
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filters?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  const onChangeSelectedTeam = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: nextTeamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.states,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      selectorData.teamIds,
      dispatch,
      tableId,
    ]
  );

  const concessionOptions = data?.filters?.concessions?.map((concession) => ({
    label: concession.name,
    value: concession.id,
  }));

  const teamOptions = data?.filters?.teams?.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  const onChangeSelectedTrack = useCallback(
    (option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: option.value,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.track = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.states,
      selectorData.teamIds,
      selectorData?.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const predefinedOrderOptions = data?.filtersData?.data?.predefinedOrders?.map(
    (predefined: { id: string; name: string }) => ({
      label: predefined.name,
      value: predefined.id,
    })
  );

  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      const dataCursor = {
        take: takePagination,
        cursor: selectorCursor?.cursor,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId: option.value,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.predefinedOrderId = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.states,
      selectorData.teamIds,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  const onClearValue = useCallback(() => {
    console.log('[CampaignTable][onClearValue] BEFORE — selectorCursor:', selectorCursor, '| selectorData:', selectorData, '| globalCursor:', globalCursor);
    inputDateRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputNameRef.current?.clear();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }
    inputTeamRef.current?.clearSelected();
    inputTrackRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();

    const dataCursor = {
      take: takePagination,
      cursor: '',
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      orderBy: 'desc' as const,
      sortField: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      name: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      name: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      sortField: undefined,
      orderBy: 'desc' as const,
      onlyFlagged: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      name: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      includeInactive: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
    }));
    setCurrentServerPage(1);
    onReFetchData?.();
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, takePagination]);

  const handleClickRow = (row: Row<dataTable>) => {
    const val: boolean = row.getIsSelected();
    if (!val) {
      setRowState(row.original || {});
    } else {
      setRowState({});
    }
  };

  // Sync rowSelection → rowState for direct radio button clicks (stopPropagation prevents onSingleTap).
  // En modo multi-selección la selección la gestiona el padre; no se sincroniza rowState.
  useEffect(() => {
    if (isMultiSelect) return;
    const selectedIds = Object.keys(rowSelection).filter((key) => (rowSelection as Record<string, boolean>)[key]);
    if (selectedIds.length > 0) {
      const selectedRow = table.getRow(selectedIds[0]);
      setRowState(selectedRow?.original || {});
    } else {
      setRowState({});
    }
  }, [rowSelection, setRowState, table, isMultiSelect]);

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = concessionOptions[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const fetchCampaignID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        name: selectorData.name ?? selectorCursor.name,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };

      if (take > 0) {
        const lastCampaign =
          dataAll[dataAll.length - 1].genericOrCampaignOrderID;
        if (lastCampaign) {
          dispatch(setCursor(tableId, dataSelector));
        }
      } else {
        const firstAccident = dataAll[0].genericOrCampaignOrderID;
        if (firstAccident) {
          dispatch(setCursor(tableId, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.name,
      selectorData.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dataAll,
    ]
  );

  const nextPagination = async () => {
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    if (
      dataAll.length >= 1 &&
      data.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchCampaignID(
        dataAll[dataAll.length - 1].genericOrCampaignOrderID as string
      );

      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    if (dataAll.length >= 1 && data.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCampaignID(
        dataAll[0].genericOrCampaignOrderID as string,
        -takePagination
      );

      onReFetchPagination();
    }
  };
  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      concessionId: selectorData?.concessionId ?? selectorCursor?.concessionId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      name: selectorData.name ?? selectorCursor.name,
      states: selectorData.states ?? selectorCursor.states,
      userIds: selectorData.userIds ?? selectorCursor.userIds,
      includeInactive:
        selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      track: selectorData.track ?? selectorCursor.track,
      pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
      pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
      pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
      pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      description:
        (selectorData.description ?? selectorCursor.description) || undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setCurrentServerPage(1);

    onReFetchData?.();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLandscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            <CampaignSearchContainer>
              <StyledTypography size="S">
                {t('common:tables.labels.campaignName')}
              </StyledTypography>
              <SearchBar
                variant="campaign"
                onChangeInput={onChangeCampaign}
                ref={inputNameRef}
                disabled={isLoadingData}
              />
            </CampaignSearchContainer>
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeDateInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              disabled={isLoadingData}
            />
            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor.concessionId ?? ''}
                options={concessionOptions as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {teamOptions && teamOptions.length > 0 && (
              <MultiSelectDropdown
                inputSize="M"
                ref={inputTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions as Options}
                onChangeSelected={onChangeSelectedTeam}
                isDisabled={isLoadingData}
              />
            )}
            <DropDownInput
              inputSize="XS"
              ref={inputTrackRef}
              labelText={t('forms:workOrder.roadType')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor.track ?? ''}
              options={trackOptions as Options}
              onChangeSelected={onChangeSelectedTrack as onChangeValue}
              isDisabled={isLoadingData}
            />
            {showPredefinedFilter && predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <MultiSelectDropdown
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />
            {userOptions && userOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}
            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(hideableColumns.map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t('common:filters.includeInactiveInCreator')}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (row.original?.id) {
                      navigate(`/work-orders/campaign/${row.original.id}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {isMultiSelect && onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={
            propsSelectedCount ??
            (selectAllMode
              ? (data?.totalCount || 0) - (excludedIds?.size || 0)
              : Object.keys(rowSelection).filter(
                  (k) => (rowSelection as Record<string, boolean>)[k]
                ).length)
          }
          totalCount={data?.totalCount || 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every(
              (row: dataTable) =>
                (rowSelection as Record<string, boolean>)[row.id as string]
            )
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      {showButtonsPagination && (
        <PaginationButtons
          pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
          currentPage={currentServerPage}
          totalCount={data?.totalCount}
          nextPage={nextPagination}
          previousPage={backPagination}
          isDisabled={isLoadingData}
          // Tarea #11: gates aplicados en `useServerPageSize` — en tablet o
          // contexto modal, los 3 props van como undefined y el selector no
          // se renderiza.
          {...paginationProps}
        />
      )}
    </ContainerStyled>
  );
};
